import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserCredential } from 'firebase/auth';
import { 
  signInWithGoogle, 
  signOutUser, 
  onAuthStateChange, 
  getUserData, 
  UserData,
  createOrUpdateUser,
  signInAnonymously as firebaseSignInAnonymously
} from '../utils/firebase';

// Auth context interface
interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  isLoading: boolean;
  isSigningIn: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signInAnonymously: () => Promise<UserCredential | null>;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auth state listener
  useEffect(() => {
    
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      console.log('🔐 Auth state changed:', { 
        hasUser: !!firebaseUser, 
        email: firebaseUser?.email,
        uid: firebaseUser?.uid 
      });
      
      setUser(firebaseUser);
      setError(null);
      
      if (firebaseUser) {
        try {
          // Get or create user data in Firestore (initial load)
          console.log('📊 Fetching user data for:', firebaseUser.uid);
          const fetchedUserData = await createOrUpdateUser(firebaseUser);
          setUserData(fetchedUserData);
          console.log('✅ User data loaded:', fetchedUserData.email);
        } catch (err) {
          console.error('❌ Error loading user data:', err);
          setError('Failed to load user data');
          setUserData(null);
        }
      } else {
        setUserData(null);
        console.log('👋 User signed out, cleared user data');
      }
      
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // Real-time Firestore listener for user data changes (e.g., subscription updates from webhook)
  useEffect(() => {
    if (!user?.uid) return;

    console.log('🔄 Setting up Firestore real-time listener for user:', user.uid);
    
    // Import onSnapshot dynamically
    import('firebase/firestore').then(({ onSnapshot, doc }) => {
      import('../utils/firebase').then(({ db }) => {
        const userRef = doc(db, 'users', user.uid);
        
        const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            console.log('🔔 Firestore real-time update:', data.plan, data.email);
            setUserData(data as UserData);
          }
        }, (error) => {
          console.error('❌ Firestore listener error:', error);
        });

        return () => {
          console.log('🛑 Cleaning up Firestore listener');
          unsubscribe();
        };
      });
    });
  }, [user?.uid]);

  // Sign in with Google
  const signIn = async (): Promise<void> => {
    if (isSigningIn) return;
    
    setIsSigningIn(true);
    setError(null);
    
    try {
      console.log('🚀 Starting Google sign-in...');
      const result = await signInWithGoogle();
      
      if (result) {
        console.log('✅ Sign-in successful:', result.email);
        // User state will be updated by the auth state listener
      }
    } catch (err: any) {
      console.error('❌ Sign-in error:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setIsSigningIn(false);
    }
  };

  // Sign out
  const signOut = async (): Promise<void> => {
    try {
      console.log('👋 Signing out...');
      await signOutUser();
      // User state will be updated by the auth state listener
    } catch (err: any) {
      console.error('❌ Sign-out error:', err);
      setError(err.message || 'Failed to sign out');
    }
  };

  // Sign in anonymously
  const signInAnonymously = async (): Promise<UserCredential | null> => {
    try {
      console.log('👤 Signing in anonymously...');
      setIsSigningIn(true);
      setError(null);
      const userCredential = await firebaseSignInAnonymously();
      // User state will be updated by the auth state listener
      console.log('✅ Anonymous sign-in completed');
      return userCredential;
    } catch (err: any) {
      console.error('❌ Anonymous sign-in error:', err);
      setError(err.message || 'Failed to sign in anonymously');
      throw err;
    } finally {
      setIsSigningIn(false);
    }
  };

  // Refresh user data
  const refreshUserData = async (): Promise<void> => {
    if (!user) return;
    
    try {
      console.log('🔄 Refreshing user data for:', user.uid);
      const freshUserData = await getUserData(user.uid);
      setUserData(freshUserData);
      console.log('✅ User data refreshed');
    } catch (err: any) {
      console.error('❌ Error refreshing user data:', err);
      setError(err.message || 'Failed to refresh user data');
    }
  };

  const contextValue: AuthContextType = {
    user,
    userData,
    isLoading,
    isSigningIn,
    error,
    signIn,
    signInAnonymously,
    signOut,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook for checking authentication status
export const useAuthStatus = () => {
  const { user, userData, isLoading } = useAuth();
  
  return {
    isAuthenticated: !!user,
    isPremium: userData?.plan === 'premium',
    isLoading,
    userId: user?.uid || null,
    userEmail: user?.email || null
  };
};